import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  Item,
  StartEquipmentFeature,
  EquipmentPack,
} from "@dndModels";
import { ChoiceSelection } from "@dndComponents/character/inventory/startingEquipment/CSE.types";

import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";
import EquipmentChoice from "@dndComponents/character/inventory/startingEquipment/CSEChoice";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character;
}

const connect_util = CharConnectUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterStartingEquipment({
  char,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [equipment_packs, setEquipmentPacks] = useState<EquipmentPack[]>([]);
  
  // Map of choice ID to its selections
  const [selections, setSelections] = useState<Record<string, ChoiceSelection>>({});

  const load = useCallback(() => {
    setFetched(true);
    setEquipmentPacks(data_util.equipment_packs);
    
    // Initialize selections from existing data
    const initialSelections: Record<string, ChoiceSelection> = {};
    
    for (const cf of char.start_equipment_features) {
      const sef = cf.floating_feature as StartEquipmentFeature;
      for (const choice of sef.start_equipment) {
        const typeItemSelections: Record<string, string> = {};
        
        // Find the selected option and extract type item selections
        const selectedOption = choice.options.find(o => o.id === choice.selected_option);
        if (selectedOption) {
          for (const item of selectedOption.items) {
            if (item.item_type === "Type" && item.selected_item_id) {
              typeItemSelections[item.id] = item.selected_item_id;
            }
          }
        }
        
        initialSelections[choice.id] = {
          selectedOptionId: choice.selected_option || "",
          typeItemSelections
        };
      }
    }
    
    setSelections(initialSelections);
  }, [char]);

  useEffect(() => {
    if (!fetched) {
      load();
    }
  }, [fetched, load]);

  const updateChoiceSelection = (choiceId: string, selectedOptionId: string) => {
    setSelections(prev => ({
      ...prev,
      [choiceId]: {
        selectedOptionId,
        typeItemSelections: {} // Reset type selections when option changes
      }
    }));
  };

  const updateTypeItemSelection = (choiceId: string, itemId: string, selectedItemId: string) => {
    setSelections(prev => ({
      ...prev,
      [choiceId]: {
        ...prev[choiceId],
        typeItemSelections: {
          ...prev[choiceId]?.typeItemSelections,
          [itemId]: selectedItemId
        }
      }
    }));
  };

  const add_starting_equipment = async () => {
    const start_equipment_features = char.start_equipment_features;
    
    for (const cf of start_equipment_features) {
      const sef = cf.floating_feature as StartEquipmentFeature;
      
      for (const choice of sef.start_equipment) {
        const choiceSelection = selections[choice.id];
        if (!choiceSelection?.selectedOptionId) continue;
        
        const option = choice.options.find(o => o.id === choiceSelection.selectedOptionId);
        if (!option) continue;
        
        for (const item of option.items) {
          switch (item.item_type) {
            case "Item": {
              const base_item = data_util.items.find(o => o._id === item.item);
              if (base_item) {
                await addItem({ item: base_item, count: item.item_count });
              }
              break;
            }
            case "Type": {
              const selectedItemId = choiceSelection.typeItemSelections[item.id];
              const base_item = data_util.items.find(o => o._id === selectedItemId);
              if (base_item) {
                await addItem({ item: base_item, count: item.item_count });
              }
              break;
            }
            case "Pack": {
              const pack = equipment_packs.find(o => o._id === item.item);
              if (pack) {
                for (const pack_item of pack.items) {
                  const base_item = data_util.items.find(o => o._id === pack_item.item_id);
                  if (base_item) {
                    await addItem({ item: base_item, count: item.item_count });
                  }
                }
              }
              break;
            }
            case "Money": {
              const char_moneys = char.money;
              switch (item.item.toLowerCase()) {
                case "pp": char_moneys.platinum += item.item_count; break;
                case "gold": case "gp": char_moneys.gold += item.item_count; break;
                case "ep": char_moneys.electrum += item.item_count; break;
                case "sp": char_moneys.silver += item.item_count; break;
                case "cp": char_moneys.copper += item.item_count; break;
              }
              break;
            }
            case "Other Item": {
              char.other_possessions += `\n${item.item}`;
              break;
            }
          }
        }
      }
    }
    char.subscription_holder.recalculate();
  }
  
  const addItem = async ({ 
    item, 
    count = 1
  }: { 
    item: Item | EquipmentPack, 
    count?: number
  }) => {
    if (item instanceof EquipmentPack) {
      for (const ep_item of item.items) {
        const the_item = data_util.items.find(o => o._id === ep_item.item_id);
        if (the_item) {
          await connect_util.addItem({ char, item: the_item, count: ep_item.count });
        }
      }
    } else {
      await connect_util.addItem({ char, item, count });
    }
  }

  // const hasIncompleteSelections = () => {
  //   for (const cf of char.start_equipment_features) {
  //     const sef = cf.floating_feature as StartEquipmentFeature;
      
  //     for (const choice of sef.start_equipment) {
  //       const choiceSelection = selections[choice.id];
        
  //       // No option selected
  //       if (!choiceSelection?.selectedOptionId) return true;
        
  //       // Find selected option
  //       const option = choice.options.find(o => o.id === choiceSelection.selectedOptionId);
  //       if (!option) continue;
        
  //       // Check if all Type items have selections
  //       for (const item of option.items) {
  //         if (item.item_type === "Type") {
  //           if (!choiceSelection.typeItemSelections[item.id]) {
  //             return true;
  //           }
  //         }
  //       }
  //     }
  //   }
  //   return false;
  // };

  if (!fetched) {
    return (<LoadingBox />);
  }

  return (
    <Grid container spacing={0} direction="column">
      <Grid>
        <span className="header">Equipment</span>
      </Grid>
      <Grid container spacing={0} direction="column">
        <Grid>Equipment Choices</Grid>
        {char.start_equipment_features.map((cf, featureIndex) => {
          const sef = cf.floating_feature as StartEquipmentFeature;
          return sef.start_equipment.map((choice, choiceIndex) => (
            <EquipmentChoice
              key={`${featureIndex}_${choiceIndex}`}
              choice={choice}
              selection={selections[choice.id] || { selectedOptionId: "", typeItemSelections: {} }}
              onOptionSelect={(optionId) => updateChoiceSelection(choice.id, optionId)}
              onTypeItemSelect={(itemId, selectedItemId) => 
                updateTypeItemSelection(choice.id, itemId, selectedItemId)
              }
              equipmentPacks={equipment_packs}
            />
          ));
        })}
        <Grid>
          <Button
            variant="contained"
            color="primary"
            onClick={add_starting_equipment}
          >
            Add Starting Equipment
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}